import { Injectable, inject } from '@angular/core';
import { TelnyxWebRTCService } from '../services/telnyx-webrtc.service';

/**
 * Integration service for Call UI permissions and initialization.
 *
 * NOTE: Incoming call modal is handled by TelnyxIntegrationService
 * to avoid duplicate modals. This service only handles permissions.
 */
@Injectable({
  providedIn: 'root'
})
export class CallUIIntegrationService {
  private telnyxService = inject(TelnyxWebRTCService);

  /**
   * Request browser notification permissions
   */
  async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  }

  /**
   * Request microphone permission for WebRTC calls
   */
  async requestMicrophonePermission(): Promise<boolean> {
    try {
      const hasPermission = await this.telnyxService.requestMicrophonePermission();
      if (hasPermission) {
        return true;
      } else {
        console.warn('Microphone permissions denied - calling may not work properly');
        return false;
      }
    } catch (error) {
      console.error('Failed to request microphone permission:', error);
      return false;
    }
  }

  /**
   * Initialize call UI integration
   * Call this method when the application starts
   */
  async initialize(): Promise<void> {
    try {
      // Request notification permissions
      await this.requestNotificationPermission();
      
      // CRITICAL: Pre-request microphone permissions for seamless calling
      // This should be done during app initialization to avoid delays during calls
      await this.requestMicrophonePermission();
    } catch (error) {
      console.error('Failed to initialize Call UI Integration Service:', error);
    }
  }
}